import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f4f5f7;
`;

const Header = styled.header`
  background-color: #ffffff;
  border-bottom: 2px solid #dfe1e6;
  padding: 1rem 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: opacity 0.1s ease-out;

  &:hover {
    opacity: 0.8;
  }
`;

const LogoIcon = styled.div`
  font-size: 1.75rem;
  line-height: 1;
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #172b4d;
  margin: 0;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
  max-width: 600px;
  background-color: #ffffff;
  padding: 3rem 2rem;
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const IconContainer = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, rgba(0, 135, 90, 0.08) 0%, rgba(54, 179, 126, 0.08) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const IconEmoji = styled.div`
  font-size: 4rem;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const ErrorCode = styled.h1`
  font-size: 4rem;
  font-weight: 500;
  margin: 0;
  color: #172b4d;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #172b4d;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Description = styled.p`
  font-size: 0.875rem;
  color: #5e6c84;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 0.5rem;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #00875a 0%, #36b37e 100%);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 3px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  box-shadow: 0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);
  line-height: 1.5;

  &:hover {
    opacity: 0.9;
    box-shadow: 0 2px 4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31);
  }

  &:active {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #4c9aff;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }
`;

const SecondaryButton = styled.button`
  background-color: rgba(9, 30, 66, 0.04);
  color: #172b4d;
  padding: 0.75rem 1.5rem;
  border-radius: 3px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  line-height: 1.5;

  &:hover {
    background-color: rgba(9, 30, 66, 0.08);
  }

  &:active {
    background-color: rgba(9, 30, 66, 0.13);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #4c9aff;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }
`;

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Logo onClick={handleGoHome}>
            <LogoIcon>📅</LogoIcon>
            <LogoText>Shiftas</LogoText>
          </Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <ContentBox>
          <IconContainer>
            <IconEmoji>🔍</IconEmoji>
          </IconContainer>

          <ErrorCode>404</ErrorCode>

          <div>
            <Title>ページが見つかりません</Title>
          </div>

          <Description>
            お探しのページは存在しないか、移動または削除された可能性があります。
            URLをご確認いただくか、ホームページに戻ってください。
          </Description>

          <ButtonGroup>
            <PrimaryButton onClick={handleGoHome}>
              ホームに戻る
            </PrimaryButton>
            <SecondaryButton onClick={handleGoBack}>
              前のページに戻る
            </SecondaryButton>
          </ButtonGroup>
        </ContentBox>
      </MainContent>
    </PageContainer>
  );
};
